import {
  IProduct,
  IProductSelectorTree,
  ISelectorNode,
  ISelectorOption,
  ISelectorValidationMessage,
  ISelectorValidationResult,
  SelectorValidationCode,
} from '@topshots/core-models';

export const createNodeId = (
  nodes: Record<string, ISelectorNode>
): string => {
  let id = `node_${Math.random().toString(36).slice(2, 8)}`;
  while (nodes[id]) {
    id = `node_${Math.random().toString(36).slice(2, 8)}`;
  }
  return id;
};

export const createOptionId = (options: ISelectorOption[]): string => {
  const existing = new Set(options.map((option) => option.id));
  let id = `opt_${Math.random().toString(36).slice(2, 8)}`;
  while (existing.has(id)) {
    id = `opt_${Math.random().toString(36).slice(2, 8)}`;
  }
  return id;
};

export const normalizeOptions = (
  options: ISelectorOption[]
): ISelectorOption[] =>
  options.map((option, index) => ({
    ...option,
    sortOrder: index,
  }));

export const sortOptions = (
  options: ISelectorOption[]
): ISelectorOption[] =>
  [...options].sort((a, b) => {
    const aOrder = a.sortOrder ?? 0;
    const bOrder = b.sortOrder ?? 0;
    return aOrder - bOrder;
  });

export const validateTree = (
  tree: IProductSelectorTree,
  productsById: Map<string, IProduct>
): ISelectorValidationResult => {
  const errors: ISelectorValidationMessage[] = [];
  const warnings: ISelectorValidationMessage[] = [];
  const nodeIds = Object.keys(tree.nodes);

  if (nodeIds.length === 0) {
    errors.push({
      code: SelectorValidationCode.TreeEmpty,
      message: 'Tree has no nodes.',
    });
    return { valid: false, errors, warnings };
  }

  if (!tree.nodes[tree.rootNodeId]) {
    errors.push({
      code: SelectorValidationCode.RootNodeMissing,
      message: `Starting question "${tree.rootNodeId}" does not exist.`,
      nodeId: tree.rootNodeId,
    });
  }

  for (const node of Object.values(tree.nodes)) {
    for (const option of node.options) {
      if (option.next.type === 'none') {
        errors.push({
          code: SelectorValidationCode.OptionNotConnected,
          message: `Option "${option.label}" in question "${node.id}" is not connected.`,
          nodeId: node.id,
          optionId: option.id,
        });
      }

      if (option.next.type === 'node') {
        if (!tree.nodes[option.next.nodeId]) {
          errors.push({
            code: SelectorValidationCode.NodeRefInvalid,
            message: `Option "${option.label}" in question "${node.id}" points to a missing question "${option.next.nodeId}".`,
            nodeId: node.id,
            optionId: option.id,
          });
        }
      }

      if (option.next.type === 'product') {
        if (!productsById.has(option.next.productId)) {
          errors.push({
            code: SelectorValidationCode.ProductRefInvalid,
            message: `Option "${option.label}" in question "${node.id}" references missing product "${option.next.productId}".`,
            nodeId: node.id,
            optionId: option.id,
            productId: option.next.productId,
          });
        }
      }
    }
  }

  const visitState = new Map<string, 'unvisited' | 'inStack' | 'done'>();
  for (const nodeId of nodeIds) {
    visitState.set(nodeId, 'unvisited');
  }

  const dfs = (nodeId: string, path: string[]): void => {
    if (visitState.get(nodeId) === 'done') return;
    if (visitState.get(nodeId) === 'inStack') {
      const cycleStart = path.indexOf(nodeId);
      const cyclePath = [...path.slice(cycleStart), nodeId];
      errors.push({
        code: SelectorValidationCode.CycleDetected,
        message: `Loop detected: "${cyclePath.join(
          ' → '
        )}" keeps users moving in circles.`,
        nodeId,
      });
      return;
    }

    const node = tree.nodes[nodeId];
    if (!node) return;

    visitState.set(nodeId, 'inStack');
    for (const option of node.options) {
      if (option.next.type === 'node' && option.next.nodeId) {
        dfs(option.next.nodeId, [...path, nodeId]);
      }
    }
    visitState.set(nodeId, 'done');
  };

  for (const nodeId of nodeIds) {
    if (visitState.get(nodeId) === 'unvisited') {
      dfs(nodeId, []);
    }
  }

  if (tree.nodes[tree.rootNodeId]) {
    const visited = new Set<string>();
    const queue: string[] = [tree.rootNodeId];

    while (queue.length > 0) {
      const current = queue.shift();
      if (!current || visited.has(current)) continue;
      visited.add(current);

      const node = tree.nodes[current];
      if (!node) continue;

      for (const option of node.options) {
        if (
          option.next.type === 'node' &&
          option.next.nodeId &&
          !visited.has(option.next.nodeId)
        ) {
          queue.push(option.next.nodeId);
        }
      }
    }

    for (const nodeId of nodeIds) {
      if (!visited.has(nodeId)) {
        const nodePrompt = tree.nodes[nodeId]?.prompt;
        warnings.push({
          code: SelectorValidationCode.OrphanNode,
          message: `Question "${
            nodePrompt || nodeId
          }" is not reachable from the starting question.`,
          nodeId,
        });
      }
    }
  }

  for (const node of Object.values(tree.nodes)) {
    if (node.options.length === 0) {
      const nodeLabel = node.prompt || node.id;
      warnings.push({
        code: SelectorValidationCode.DeadEndNode,
        message: `Question "${nodeLabel}" has no options (dead end).`,
        nodeId: node.id,
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
};
